/**
 * @file index.ts
 * @description Библиотека заготовок секций платформы (phase19.16 Р2; SB27;
 * uiux-design-r2 §5): реестр в порядке групп и поиск по ключу. Р2а — группа
 * «Страница товара» и отбор по контексту (`sectionPresetsFor`).
 */

import type { SectionContext } from "../contexts.js";
import { SECTION_PRESETS_CTA_TO_EMPTY } from "./catalog-more.js";
import { SECTION_PRESETS_HERO_TO_TEXT } from "./catalog.js";
import { SECTION_PRESETS_UNIVERSAL } from "./catalog-universal.js";
import { SECTION_PRESETS_PRODUCT } from "./product.js";
import { SECTION_PRESET_GROUPS, type SectionPreset } from "./types.js";

export * from "./types.js";

const GROUP_ORDER: readonly string[] = SECTION_PRESET_GROUPS.map((group) => group.key);

/**
 * Все заготовки (Р2 — 31, Р2а — ещё 3 раскладки страницы товара) в порядке
 * групп библиотеки; внутри группы — порядок файлов. Сортировка устойчивая:
 * новая заготовка встаёт в свою группу без перестановки файлов. Кабинет и
 * сервер берут список для места через `sectionPresetsFor(context)`.
 */
export const SECTION_PRESETS: readonly SectionPreset[] = [
  ...SECTION_PRESETS_HERO_TO_TEXT,
  ...SECTION_PRESETS_CTA_TO_EMPTY,
  ...SECTION_PRESETS_UNIVERSAL,
  ...SECTION_PRESETS_PRODUCT,
].sort((a, b) => GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group));

/** Заготовки, которые можно вставить в контексте (обычная страница, место, шаблон товара). */
export function sectionPresetsFor(context: SectionContext): SectionPreset[] {
  return SECTION_PRESETS.filter((preset) => preset.contexts.includes(context));
}

/** Группы библиотеки, в которых в контексте есть хотя бы одна заготовка. */
export function sectionPresetGroupsFor(context: SectionContext): (typeof SECTION_PRESET_GROUPS)[number][] {
  const presets = sectionPresetsFor(context);
  return SECTION_PRESET_GROUPS.filter((group) => presets.some((preset) => preset.group === group.key));
}

/**
 * Заготовка по ключу; undefined — нет такой (сервер отвечает 400). С
 * контекстом — только разрешённая в нём (иначе тоже undefined).
 */
export function findSectionPreset(key: string, context?: SectionContext): SectionPreset | undefined {
  const preset = SECTION_PRESETS.find((p) => p.key === key);
  if (!preset || (context !== undefined && !preset.contexts.includes(context))) return undefined;
  return preset;
}
